import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { API } from "../lib/constants";
import {
  ShieldCheck,
  ArrowRight,
  ArrowLeft,
  Lock,
  AlertTriangle,
  CheckCircle2,
  User,
  Car,
  Users,
  Shield,
  MapPin,
  Loader2,
} from "lucide-react";

const TOTAL_STEPS = 7;

const STEP_META = [
  { num: 1, label: "Fault", icon: ShieldCheck },
  { num: 2, label: "Location", icon: MapPin },
  { num: 3, label: "Contact", icon: User },
  { num: 4, label: "Vehicle", icon: Car },
  { num: 5, label: "Passengers", icon: Users },
  { num: 6, label: "Police", icon: Shield },
  { num: 7, label: "Review", icon: CheckCircle2 },
];

const initialForm = {
  at_fault: null, // null | true | false
  state: "",
  state_other: "",
  accident_date: "",
  accident_location: "",
  full_name: "",
  phone: "",
  email: "",
  address: "",
  vehicle_year: "",
  vehicle_make: "",
  vehicle_model: "",
  tag_number: "",
  insurance_company: "",
  passenger_count: 0,
  had_children: false,
  had_injuries: false,
  injuries_description: "",
  police_called: false,
  police_report_obtained: false,
  case_number: "",
  notes: "",
};

// ---------- Reusable UI ----------

const Label = ({ children, htmlFor, required }) => (
  <label
    htmlFor={htmlFor}
    className="block text-xs uppercase tracking-[0.18em] font-semibold text-[#4B5563] mb-1.5"
  >
    {children}
    {required && <span className="text-[#D32F2F] ml-1">*</span>}
  </label>
);

const TextInput = ({ id, value, onChange, type = "text", placeholder, testid, required, maxLength }) => (
  <input
    id={id}
    type={type}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testid}
    required={required}
    maxLength={maxLength}
    className="w-full bg-white border border-[#E2E8F0] rounded-md px-3.5 py-2.5 text-[#111827] placeholder:text-[#9CA3AF] focus:ring-2 focus:ring-[#0B192C] focus:border-[#0B192C] focus:outline-none shadow-sm transition"
  />
);

const TextArea = ({ id, value, onChange, placeholder, testid, rows = 3 }) => (
  <textarea
    id={id}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testid}
    rows={rows}
    className="w-full bg-white border border-[#E2E8F0] rounded-md px-3.5 py-2.5 text-[#111827] placeholder:text-[#9CA3AF] focus:ring-2 focus:ring-[#0B192C] focus:border-[#0B192C] focus:outline-none shadow-sm resize-y transition"
  />
);

const CardChoice = ({ active, onClick, title, desc, icon: Icon, testid, danger }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-left p-5 rounded-xl border-2 transition-all bg-white ${
      active
        ? danger
          ? "border-[#D32F2F] bg-[#FEF2F2]"
          : "border-[#0B192C] bg-[#F1F5F9]"
        : "border-[#E2E8F0] hover:border-[#0B192C]/60"
    }`}
  >
    <div className="flex items-center gap-3">
      {Icon && (
        <Icon
          className={`h-5 w-5 ${
            active ? (danger ? "text-[#D32F2F]" : "text-[#0B192C]") : "text-[#4B5563]"
          }`}
        />
      )}
      <div className="font-semibold text-[#0B192C]">{title}</div>
    </div>
    {desc && <div className="text-sm text-[#4B5563] mt-1.5">{desc}</div>}
  </button>
);

const YesNo = ({ value, onChange, idBase }) => (
  <div className="grid grid-cols-2 gap-3">
    <button
      type="button"
      data-testid={`${idBase}-yes`}
      onClick={() => onChange(true)}
      className={`py-3 rounded-md font-semibold border-2 transition-all ${
        value === true
          ? "border-[#0B192C] bg-[#0B192C] text-white"
          : "border-[#E2E8F0] bg-white text-[#0B192C] hover:border-[#0B192C]/60"
      }`}
    >
      Yes
    </button>
    <button
      type="button"
      data-testid={`${idBase}-no`}
      onClick={() => onChange(false)}
      className={`py-3 rounded-md font-semibold border-2 transition-all ${
        value === false
          ? "border-[#0B192C] bg-[#0B192C] text-white"
          : "border-[#E2E8F0] bg-white text-[#0B192C] hover:border-[#0B192C]/60"
      }`}
    >
      No
    </button>
  </div>
);

// ---------- Main ----------

export default function Intake() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setForm((f) => ({ ...f, referred_by: ref }));
    }
  }, [searchParams]);

  const progress = Math.round(((step - 1) / (TOTAL_STEPS - 1)) * 100);

  const canProceed = () => {
    switch (step) {
      case 1:
        return form.at_fault === false;
      case 2:
        return (
          form.state &&
          (form.state !== "Other" || form.state_other.trim().length > 0)
        );
      case 3:
        return (
          form.full_name.trim() &&
          form.phone.trim() &&
          form.email.trim() &&
          form.address.trim()
        );
      case 4:
        return (
          form.vehicle_make.trim() &&
          form.vehicle_model.trim() &&
          form.tag_number.trim() &&
          form.insurance_company.trim()
        );
      case 5:
        return form.passenger_count !== null && form.passenger_count !== "";
      case 6:
        return form.police_called !== null;
      case 7:
        return true;
      default:
        return false;
    }
  };

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        passenger_count: Number(form.passenger_count) || 0,
      };
      const res = await axios.post(`${API}/submissions`, payload);
      toast.success("Submission received");
      navigate(`/thank-you?id=${res.data.id}`);
    } catch (e) {
      const detail =
        e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    if (!canProceed()) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (step === TOTAL_STEPS) {
      onSubmit();
    } else {
      setStep((s) => Math.min(TOTAL_STEPS, s + 1));
    }
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]" data-testid="intake-page">
      <Header />

      <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Progress */}
        <div className="mb-8" data-testid="wizard-progress">
          <div className="flex justify-between mb-3">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B192C]">
              Step {step} of {TOTAL_STEPS} · {STEP_META[step - 1].label}
            </div>
            <div className="text-xs text-[#4B5563]">{progress}%</div>
          </div>
          <div className="h-2 w-full bg-[#E2E8F0] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#0B192C] transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-6 sm:p-10 step-enter" key={step}>
          {step === 1 && (
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B192C] tracking-tight">
                Were you at fault in the accident?
              </h2>
              <p className="text-[#4B5563] mt-2">
                Car Hurt Help only assists drivers who were not the at-fault party.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-4">
                <CardChoice
                  active={form.at_fault === false}
                  onClick={() => update({ at_fault: false })}
                  title="I was NOT at fault"
                  desc="Someone else caused the accident."
                  icon={ShieldCheck}
                  testid="fault-not-at-fault"
                />
                <CardChoice
                  active={form.at_fault === true}
                  onClick={() => update({ at_fault: true })}
                  title="I was at fault"
                  desc="I caused the accident."
                  icon={AlertTriangle}
                  testid="fault-at-fault"
                  danger
                />
              </div>

              {form.at_fault === true && (
                <div
                  data-testid="at-fault-block"
                  className="mt-8 rounded-xl border-2 border-[#D32F2F] bg-[#FEF2F2] p-6"
                >
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-[#D32F2F] flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-display text-xl font-bold text-[#7F1D1D]">
                        We're unable to continue your intake
                      </h3>
                      <p className="text-sm text-[#7F1D1D] mt-2">
                        Car Hurt Help currently only assists drivers who were{" "}
                        <span className="font-semibold">not at fault</span>. Please
                        contact your own insurance carrier directly. We wish you a
                        speedy recovery.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B192C] tracking-tight">
                Where did the accident happen?
              </h2>
              <p className="text-[#4B5563] mt-2">
                We primarily serve Georgia and South Carolina.
              </p>

              <div className="mt-8 space-y-3">
                {[
                  { key: "Georgia", desc: "Accident occurred in Georgia." },
                  { key: "South Carolina", desc: "Accident occurred in South Carolina." },
                  { key: "Other", desc: "A different state — we'll still review your details." },
                ].map((opt) => (
                  <CardChoice
                    key={opt.key}
                    active={form.state === opt.key}
                    onClick={() => update({ state: opt.key })}
                    title={opt.key}
                    desc={opt.desc}
                    icon={MapPin}
                    testid={`state-${opt.key.toLowerCase().replace(" ", "-")}`}
                  />
                ))}
              </div>

              {form.state === "Other" && (
                <div className="mt-6">
                  <Label htmlFor="state-other" required>
                    Which state?
                  </Label>
                  <TextInput
                    id="state-other"
                    value={form.state_other}
                    onChange={(v) => update({ state_other: v })}
                    placeholder="e.g., Florida"
                    testid="state-other-input"
                  />
                  <p className="text-xs text-[#D32F2F] mt-2">
                    Heads up — we'll still capture your info, but our team focuses on
                    Georgia and South Carolina cases.
                  </p>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-5 mt-6">
                <div>
                  <Label htmlFor="acc-date">Date of accident (optional)</Label>
                  <TextInput
                    id="acc-date"
                    type="date"
                    value={form.accident_date}
                    onChange={(v) => update({ accident_date: v })}
                    testid="accident-date-input"
                  />
                </div>
                <div>
                  <Label htmlFor="acc-loc">Where (optional)</Label>
                  <TextInput
                    id="acc-loc"
                    value={form.accident_location}
                    onChange={(v) => update({ accident_location: v })}
                    placeholder="Intersection or address"
                    testid="accident-location-input"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B192C] tracking-tight">
                Your contact info
              </h2>
              <p className="text-[#4B5563] mt-2">
                We'll use this to reach you for follow-up.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <Label htmlFor="full-name" required>Full name</Label>
                  <TextInput
                    id="full-name"
                    value={form.full_name}
                    onChange={(v) => update({ full_name: v })}
                    placeholder="Jane Doe"
                    testid="full-name-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone" required>Phone number</Label>
                  <TextInput
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(v) => update({ phone: v })}
                    placeholder="(555) 123-4567"
                    testid="phone-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email" required>Email</Label>
                  <TextInput
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(v) => update({ email: v })}
                    placeholder="you@email.com"
                    testid="email-input"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="address" required>Home address</Label>
                  <TextInput
                    id="address"
                    value={form.address}
                    onChange={(v) => update({ address: v })}
                    placeholder="Street, City, State, ZIP"
                    testid="address-input"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B192C] tracking-tight">
                Vehicle & insurance
              </h2>
              <p className="text-[#4B5563] mt-2">
                Tell us about the vehicle involved.
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="v-year">Year (optional)</Label>
                  <TextInput
                    id="v-year"
                    value={form.vehicle_year}
                    onChange={(v) => update({ vehicle_year: v })}
                    placeholder="2021"
                    testid="vehicle-year-input"
                  />
                </div>
                <div>
                  <Label htmlFor="v-make" required>Make</Label>
                  <TextInput
                    id="v-make"
                    value={form.vehicle_make}
                    onChange={(v) => update({ vehicle_make: v })}
                    placeholder="Toyota"
                    testid="vehicle-make-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="v-model" required>Model</Label>
                  <TextInput
                    id="v-model"
                    value={form.vehicle_model}
                    onChange={(v) => update({ vehicle_model: v })}
                    placeholder="Camry"
                    testid="vehicle-model-input"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="tag" required>Tag (plate) number</Label>
                  <TextInput
                    id="tag"
                    value={form.tag_number}
                    onChange={(v) => update({ tag_number: v.toUpperCase() })}
                    placeholder="ABC1234"
                    testid="tag-input"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="ins" required>Insurance company</Label>
                  <TextInput
                    id="ins"
                    value={form.insurance_company}
                    onChange={(v) => update({ insurance_company: v })}
                    placeholder="e.g., State Farm, GEICO, Progressive"
                    testid="insurance-input"
                    required
                  />
                </div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B192C] tracking-tight">
                Passengers & injuries
              </h2>
              <p className="text-[#4B5563] mt-2">
                Was anyone else in the car? Was anyone hurt?
              </p>
              <div className="mt-8 grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="pass-count">How many passengers were with you?</Label>
                  <TextInput
                    id="pass-count"
                    type="number"
                    value={form.passenger_count}
                    onChange={(v) => update({ passenger_count: v.replace(/[^0-9]/g, "") })}
                    placeholder="0"
                    testid="passenger-count-input"
                  />
                </div>
                <div>
                  <Label>Were any of them children?</Label>
                  <YesNo
                    value={form.had_children}
                    onChange={(v) => update({ had_children: v })}
                    idBase="had-children"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Were there any injuries?</Label>
                  <YesNo
                    value={form.had_injuries}
                    onChange={(v) => update({ had_injuries: v })}
                    idBase="had-injuries"
                  />
                </div>
                {form.had_injuries === true && (
                  <div className="sm:col-span-2">
                    <Label htmlFor="injuries">Describe the injuries</Label>
                    <TextArea
                      id="injuries"
                      value={form.injuries_description}
                      onChange={(v) => update({ injuries_description: v })}
                      placeholder="e.g., neck pain, broken arm, taken to ER..."
                      testid="injuries-description-input"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 6 && (
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B192C] tracking-tight">
                Police & report
              </h2>
              <p className="text-[#4B5563] mt-2">
                Was the police called? Did you get a report? Case number is optional —
                you can skip it.
              </p>
              <div className="mt-8 space-y-6">
                <div>
                  <Label>Was the police called to the scene?</Label>
                  <YesNo
                    value={form.police_called}
                    onChange={(v) =>
                      update({
                        police_called: v,
                        ...(v === false
                          ? { police_report_obtained: false, case_number: "" }
                          : {}),
                      })
                    }
                    idBase="police-called"
                  />
                </div>
                {form.police_called === true && (
                  <>
                    <div>
                      <Label>Did you receive a police report?</Label>
                      <YesNo
                        value={form.police_report_obtained}
                        onChange={(v) => update({ police_report_obtained: v })}
                        idBase="police-report"
                      />
                    </div>
                    <div>
                      <Label htmlFor="case-num">Case number (optional — skip if you don't have it)</Label>
                      <TextInput
                        id="case-num"
                        value={form.case_number}
                        onChange={(v) => update({ case_number: v })}
                        placeholder="e.g., 24-12345"
                        testid="case-number-input"
                      />
                    </div>
                  </>
                )}
                <div>
                  <Label htmlFor="notes">Anything else you'd like us to know? (optional)</Label>
                  <TextArea
                    id="notes"
                    value={form.notes}
                    onChange={(v) => update({ notes: v })}
                    placeholder="Add any other details that may help our team."
                    testid="notes-input"
                    rows={4}
                  />
                </div>
              </div>
            </div>
          )}

          {step === 7 && (
            <div data-testid="wizard-review">
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0B192C] tracking-tight">
                Review & submit
              </h2>
              <p className="text-[#4B5563] mt-2">
                Take a quick look. You can go back to fix anything.
              </p>

              <div className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
                <ReviewRow label="Not at fault" value={form.at_fault === false ? "Yes" : "—"} />
                <ReviewRow
                  label="State"
                  value={
                    form.state === "Other"
                      ? `Other — ${form.state_other || "—"}`
                      : form.state || "—"
                  }
                />
                <ReviewRow label="Accident date" value={form.accident_date || "—"} />
                <ReviewRow label="Accident location" value={form.accident_location || "—"} />
                <ReviewRow label="Full name" value={form.full_name} />
                <ReviewRow label="Phone" value={form.phone} />
                <ReviewRow label="Email" value={form.email} />
                <ReviewRow label="Address" value={form.address} />
                <ReviewRow
                  label="Vehicle"
                  value={`${form.vehicle_year || ""} ${form.vehicle_make} ${form.vehicle_model}`.trim()}
                />
                <ReviewRow label="Tag" value={form.tag_number} />
                <ReviewRow label="Insurance" value={form.insurance_company} />
                <ReviewRow label="Passengers" value={form.passenger_count || 0} />
                <ReviewRow label="Children" value={form.had_children ? "Yes" : "No"} />
                <ReviewRow label="Injuries" value={form.had_injuries ? "Yes" : "No"} />
                {form.had_injuries && (
                  <ReviewRow label="Injury details" value={form.injuries_description || "—"} />
                )}
                <ReviewRow label="Police called" value={form.police_called ? "Yes" : "No"} />
                <ReviewRow
                  label="Police report"
                  value={form.police_report_obtained ? "Yes" : "No"}
                />
                <ReviewRow label="Case number" value={form.case_number || "—"} />
                {form.notes && <ReviewRow label="Notes" value={form.notes} />}
              </div>

              <div className="mt-8 flex items-center gap-2 text-xs text-[#4B5563]">
                <Lock className="h-4 w-4" />
                Submitting confirms the information above is accurate to the best of your
                knowledge.
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="mt-10 flex items-center justify-between gap-3 pt-6 border-t border-[#E2E8F0]">
            <button
              type="button"
              onClick={back}
              disabled={step === 1}
              data-testid="wizard-back-button"
              className="inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-[#0B192C] hover:bg-[#F1F5F9] disabled:opacity-30 disabled:cursor-not-allowed transition-colors font-semibold"
            >
              <ArrowLeft className="h-4 w-4" /> Back
            </button>

            {form.at_fault !== true && (
              <button
                type="button"
                onClick={next}
                disabled={submitting}
                data-testid="wizard-next-button"
                className="inline-flex items-center gap-2 rounded-md bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-semibold px-6 py-3 shadow-sm transition-colors disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Submitting...
                  </>
                ) : step === TOTAL_STEPS ? (
                  <>Submit <CheckCircle2 className="h-4 w-4" /></>
                ) : (
                  <>Continue <ArrowRight className="h-4 w-4" /></>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-[#4B5563]">
          <Lock className="h-3.5 w-3.5" /> Confidential & Secure · SSL encrypted
        </div>
      </main>

      <Footer />
    </div>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-[#E2E8F0] py-2">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#4B5563] font-semibold">
        {label}
      </div>
      <div className="text-right font-semibold text-[#0B192C] break-words max-w-[60%]">
        {String(value || "—")}
      </div>
    </div>
  );
}
